import { isEmpty, keyBy } from 'lodash-es';
import {
  BONUSES_SECTION_ACTIVE,
  BONUSES_SECTION_AVAILABLE,
  PROMO_TYPES
} from '../../constants';
import checkIsActivePromo from '../../utils/checkIsActivePromo';
import { PromotionsApi } from '../types';
import {
  ParsedTournaments,
  Promo,
  PromoCategories,
  PromoCategory,
  PromoConfig,
  PromoConfigItemParsed,
  PromoItem,
  PromoTabsConfig,
  PromoType,
  RecentlyCompletedPromoConfig,
  Tournament
} from './types';

const defaultTabsOrder = [
  PROMO_TYPES.promotion,
  PROMO_TYPES.bonus,
  PROMO_TYPES.tournament,
  PROMO_TYPES.widget
];

export const normalizePromoTabs = (config: PromoConfig = {}) => {
  const normalizedTabs: Record<string, PromoConfigItemParsed> = {};
  const normalizedConfig = normalizePromotionsConfig(config);

  const tabsPosition = normalizedConfig.tabsPosition?.map((value) =>
    value.toLowerCase()
  );
  const currentTabsOrder = tabsPosition?.length
    ? tabsPosition
    : defaultTabsOrder;

  for (const tab of currentTabsOrder) {
    const tabData = normalizedConfig[tab as keyof PromoTabsConfig];

    const normalizedList = tabData?.categories?.map(
      (category: PromoCategory) => ({
        ...category,
        list: keyBy(category.list, 'itemId')
      })
    );

    const normalizedCategories: PromoCategories = keyBy(
      normalizedList,
      'categoryName'
    );

    normalizedTabs[tab] = {
      ...tabData,
      categories: normalizedCategories
    };
  }

  return normalizedTabs;
};

const normalizePromotionsConfig = (config: PromoConfig = {}) => {
  const normalizedTabsConfig = defaultTabsOrder.reduce((acc, tab: string) => {
    const currentTabConfig = config[tab as keyof PromoTabsConfig];
    const {
      list,
      categories,
      showAvailableActiveCategories = false
    } = currentTabConfig || {};

    return {
      ...acc,
      [tab]: {
        ...currentTabConfig,
        ...(showAvailableActiveCategories
          ? {
              categories: [
                {
                  categoryName: BONUSES_SECTION_AVAILABLE,
                  ...(list?.length && { list })
                },
                {
                  categoryName: BONUSES_SECTION_ACTIVE
                }
              ]
            }
          : { categories })
      }
    };
  }, {});

  return { ...config, ...normalizedTabsConfig };
};

export const parseRecentlyPromoConfig = (
  config: RecentlyCompletedPromoConfig
) => {
  const normalizedConfig: RecentlyCompletedPromoConfig = {};

  for (const [key, value] of Object.entries(config)) {
    normalizedConfig[key] = {
      ...value,
      list: keyBy(value.list, 'itemId')
    };
  }

  return normalizedConfig;
};

export const parsePromotionsApi = (
  promotionsApi: PromotionsApi,
  activePromoType: string,
  filteredSegments: (segments: string[]) => boolean,
  listOfSelectedPromo: { [itemId: string]: PromoItem }
): Promo[] =>
  Object.values(promotionsApi).filter((promo) => {
    const { enabled, showFrom, showTo, type, promoId } = promo;
    const currentPromoSegments = listOfSelectedPromo[promoId]?.userSegments;
    const isActivePromo = checkIsActivePromo(showFrom, showTo, false);
    const isPromoIncludedInSegments = filteredSegments(currentPromoSegments);
    const isCorrectType = PROMO_TYPES[type as PromoType] === activePromoType;

    return enabled &&
      isActivePromo &&
      isCorrectType &&
      isPromoIncludedInSegments
      ? promo
      : null;
  });

export const parseTournaments = (
  tournaments: Tournament[]
): ParsedTournaments => {
  const parsedTournaments: ParsedTournaments = {};

  if (isEmpty(tournaments)) {
    return parsedTournaments;
  }

  for (const tournament of tournaments) {
    parsedTournaments[tournament.id] = tournament;
  }

  return parsedTournaments;
};

export const parsePromoBonusesApi = (
  bonuses: PromotionsApi,
  filteredSegments: (segments: string[]) => boolean,
  listOfSelectedPromo: { [itemId: string]: PromoItem }
): Promo[] =>
  Object.values(bonuses).filter((bonus) => {
    const { enabled, showFrom, showTo, promoId } = bonus;
    const currentPromoSegments = listOfSelectedPromo[promoId]?.userSegments;
    const isActivePromo = checkIsActivePromo(showFrom, showTo, false);
    const isPromoIncludedInSegments = filteredSegments(currentPromoSegments);

    return enabled && isActivePromo && isPromoIncludedInSegments ? bonus : null;
  });
